import React, { useState } from 'react';
import Navigation from '../components/navigation';
import { Footer } from '../components/footer';
import { Phone, Mail, MapPin, Send, Upload, X } from 'lucide-react';
import { motion } from 'framer-motion';
import { companyInfo } from '../data/mock';
import { useLanguage } from '../context/languagecontext';
import { useNavigate } from 'react-router-dom';

const ContactPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    service: '',
    address: '',
    message: '',
    photos: []
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    setFormData({ ...formData, photos: [...formData.photos, ...files] });
  };

  const removePhoto = (index) => {
    const updated = formData.photos.filter((_, i) => i !== index);
    setFormData({ ...formData, photos: updated });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const data = new FormData();
    data.append('form-name', 'quotes');
    data.append('name', formData.name);
    data.append('phone', formData.phone);
    data.append('email', formData.email);
    data.append('service', formData.service);
    data.append('address', formData.address);
    data.append('message', formData.message);
    formData.photos.forEach((photo) => {
      data.append('photos', photo);
    });

    try {
      await fetch('/', {
        method: 'POST',
        body: data,
      });
      navigate('/thank-you');
    } catch (err) {
      console.error('Form submission error:', err);
      navigate('/thank-you');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation />

      <div className="relative bg-slate-900 text-white py-24 mt-20">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-heading text-5xl md:text-6xl font-bold mb-6 uppercase tracking-wide"
          >
            {t('contact.title')}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="font-body text-xl text-gray-300 max-w-3xl mx-auto"
          >
            {t('contact.subtitle')}
          </motion.p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-3 gap-12">

          <div className="space-y-4">
            <h2 className="font-heading text-3xl font-bold text-slate-900 mb-8 uppercase tracking-wide">
              {t('contact.contactInfo')}
            </h2>

            <div className="flex items-center gap-4 p-5 bg-white border border-slate-200 rounded-sm shadow-sm">
              <div className="w-12 h-12 bg-orange-500 rounded-sm flex items-center justify-center flex-shrink-0">
                <Phone className="w-6 h-6 text-white" />
              </div>
              <div>
                <div className="font-body text-xs text-gray-500 uppercase tracking-wider mb-1">
                  {t('contact.phonePrimary')}
                </div>
                <div className="font-heading font-bold text-slate-900">{companyInfo.phone}</div>
              </div>
            </div>

            <div className="flex items-center gap-4 p-5 bg-white border border-slate-200 rounded-sm shadow-sm">
              <div className="w-12 h-12 bg-orange-500 rounded-sm flex items-center justify-center flex-shrink-0">
                <Phone className="w-6 h-6 text-white" />
              </div>
              <div>
                <div className="font-body text-xs text-gray-500 uppercase tracking-wider mb-1">
                  {t('contact.phoneSecondary')}
                </div>
                <div className="font-heading font-bold text-slate-900">{companyInfo.phoneSecondary}</div>
              </div>
            </div>

            <div className="flex items-center gap-4 p-5 bg-white border border-slate-200 rounded-sm shadow-sm">
              <div className="w-12 h-12 bg-orange-500 rounded-sm flex items-center justify-center flex-shrink-0">
                <Mail className="w-6 h-6 text-white" />
              </div>
              <div>
                <div className="font-body text-xs text-gray-500 uppercase tracking-wider mb-1">
                  {t('contact.email')}
                </div>
                <div className="font-heading font-bold text-slate-900">{companyInfo.email}</div>
              </div>
            </div>

            <div className="flex items-center gap-4 p-5 bg-white border border-slate-200 rounded-sm shadow-sm">
              <div className="w-12 h-12 bg-orange-500 rounded-sm flex items-center justify-center flex-shrink-0">
                <MapPin className="w-6 h-6 text-white" />
              </div>
              <div>
                <div className="font-body text-xs text-gray-500 uppercase tracking-wider mb-1">
                  {t('contact.serviceArea')}
                </div>
                <div className="font-heading font-bold text-slate-900">{companyInfo.serviceArea}</div>
              </div>
            </div>

            <div className="p-5 bg-orange-50 border border-orange-200 rounded-sm">
              <p className="font-body text-sm text-orange-700 leading-relaxed">
                {t('contact.emergency')}
              </p>
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="bg-white border border-slate-200 rounded-sm shadow-lg p-8">
              <form
                onSubmit={handleSubmit}
                className="space-y-6"
                name="quotes"
                method="POST"
                data-netlify="true"
                netlify-honeypot="bot-field"
              >
                <input type="hidden" name="form-name" value="quotes" />
                <input type="hidden" name="bot-field" />

                <h2 className="font-heading text-3xl font-bold text-slate-900 mb-8 uppercase tracking-wide">
                  {t('contact.formTitle')}
                </h2>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block font-body text-sm font-bold text-slate-700 uppercase tracking-wider mb-2">
                      {t('contact.formName')} *
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full border-2 border-slate-200 focus:border-orange-500 rounded-sm px-4 py-3 font-body text-slate-900 outline-none transition-colors"
                      placeholder="John Smith"
                    />
                  </div>
                  <div>
                    <label className="block font-body text-sm font-bold text-slate-700 uppercase tracking-wider mb-2">
                      {t('contact.formPhone')} *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full border-2 border-slate-200 focus:border-orange-500 rounded-sm px-4 py-3 font-body text-slate-900 outline-none transition-colors"
                      placeholder="(780) 000-0000"
                    />
                  </div>
                </div>

                <div>
                  <label className="block font-body text-sm font-bold text-slate-700 uppercase tracking-wider mb-2">
                    {t('contact.formEmail')}
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full border-2 border-slate-200 focus:border-orange-500 rounded-sm px-4 py-3 font-body text-slate-900 outline-none transition-colors"
                    placeholder="email@example.com"
                  />
                </div>

                <div>
                  <label className="block font-body text-sm font-bold text-slate-700 uppercase tracking-wider mb-2">
                    {t('contact.formService')}
                  </label>
                  <select
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    className="w-full border-2 border-slate-200 focus:border-orange-500 rounded-sm px-4 py-3 font-body text-slate-900 outline-none transition-colors bg-white"
                  >
                    <option value="">{t('contact.selectService')}</option>
                    <option value="window-expansion">Window & Door Expansions</option>
                    <option value="window-installation">Full Window Installation</option>
                    <option value="floor-cutting">Floor & Slab Cutting</option>
                    <option value="demolition">Concrete Demolition</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block font-body text-sm font-bold text-slate-700 uppercase tracking-wider mb-2">
                    {t('contact.formAddress')}
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    className="w-full border-2 border-slate-200 focus:border-orange-500 rounded-sm px-4 py-3 font-body text-slate-900 outline-none transition-colors"
                    placeholder="123 Main St, Edmonton, AB"
                  />
                </div>

                <div>
                  <label className="block font-body text-sm font-bold text-slate-700 uppercase tracking-wider mb-2">
                    {t('contact.formDetails')}
                  </label>
                  <textarea
                    name="message"
                    rows={4}
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full border-2 border-slate-200 focus:border-orange-500 rounded-sm px-4 py-3 font-body text-slate-900 outline-none transition-colors resize-none"
                    placeholder="Describe your project..."
                  />
                </div>

                <div>
                  <label className="block font-body text-sm font-bold text-slate-700 uppercase tracking-wider mb-2">
                    {t('contact.formPhotos')}
                  </label>
                  <p className="font-body text-xs text-gray-500 mb-2">
                    {t('contact.formPhotosDesc')}
                  </p>
                  <label className="flex items-center gap-3 border-2 border-dashed border-slate-300 hover:border-orange-500 rounded-sm px-4 py-4 cursor-pointer transition-colors">
                    <Upload className="w-5 h-5 text-slate-400" />
                    <div>
                      <span className="font-body text-slate-600 text-sm font-bold">
                        {t('contact.formUpload')}
                      </span>
                      <span className="font-body text-slate-400 text-xs block">
                        {t('contact.formUploadHint')}
                      </span>
                    </div>
                    <input
                      type="file"
                      name="photos"
                      multiple
                      accept="image/*"
                      onChange={handlePhotoUpload}
                      className="hidden"
                    />
                  </label>
                  {formData.photos.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {formData.photos.map((photo, index) => (
                        <div key={index} className="flex items-center gap-2 bg-orange-50 border border-orange-200 rounded-sm px-3 py-1">
                          <span className="font-body text-xs text-orange-700 truncate max-w-[120px]">
                            {photo.name}
                          </span>
                          <button type="button" onClick={() => removePhoto(index)}>
                            <X className="w-3 h-3 text-orange-500" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-3 bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300 text-white font-bold uppercase tracking-wider rounded-sm py-4 transition-all"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                  {loading ? t('contact.formSubmitting') : t('contact.formSubmit')}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ContactPage;
